/*
 * SHIN B2B session — runs on EVERY page.
 *
 * Why this exists: the store uses Shopify's hosted, passwordless customer accounts, so
 * a buyer who signs in through SHIN has NO Shopify session. Liquid's `customer` object
 * is therefore always nil for our buyers, and any gate written as {% if customer %}
 * would hide prices from the very people who are entitled to see them.
 *
 * The gate is resolved here instead: ask the middleware who this visitor is, publish the
 * tier on <html data-b2b-tier>, and only then fill in prices.
 *
 * Prices are NOT rendered into the page for guests — the markup ships a static
 * placeholder, and the real value is fetched client-side from Shopify's own
 * /products/{handle}.js only once the tier is 'verified'. So a guest (or a crawler)
 * never receives a real price in the HTML.
 */

(() => {
  const API_BASE = (window.SUMITOMO_API_BASE || '').replace(/\/$/, '');
  const TIERS = ['guest', 'pending', 'verified'];

  const state = { tier: 'guest', customer: null, loaded: false };
  window.SumitomoSession = state;

  const publish = () => {
    document.documentElement.dataset.b2bTier = state.tier;
    document.dispatchEvent(new CustomEvent('sumitomo:session', { detail: { ...state } }));
  };

  async function loadSession() {
    if (!API_BASE) return publish();
    try {
      const res = await fetch(`${API_BASE}/session`, { credentials: 'include', headers: { Accept: 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        state.tier = TIERS.includes(data.tier) ? data.tier : 'guest';
        state.customer = data.customer || null;
      } else {
        // We reached the server and it failed: still a guest, but that is OUR fault and
        // the buyer deserves to know why prices vanished.
        state.degraded = true;
      }
    } catch (e) {
      // Middleware unreachable: stay a guest. Failing closed keeps prices hidden, but a
      // signed-in buyer must not be left thinking their account stopped working.
      state.degraded = true;
    }
    state.loaded = true;
    publish();
    if (state.degraded) showDegradedNotice();
  }

  /** One quiet, dismissible line — not a modal, and never on top of the page content. */
  function showDegradedNotice() {
    if (document.querySelector('[data-session-degraded]')) return;
    const host = document.querySelector('[data-session-notice]');
    const message = (host && host.dataset.message) || '';
    if (!message) return;

    const bar = document.createElement('div');
    bar.setAttribute('data-session-degraded', '');
    bar.setAttribute('role', 'status');
    bar.style.cssText =
      'position:fixed;left:0;right:0;bottom:0;z-index:60;background:#fff4f3;border-top:3px solid #ba1a1a;' +
      'color:#7a1216;font-family:Gotham,Verdana,sans-serif;font-size:13px;padding:12px 16px;display:flex;' +
      'gap:16px;align-items:center;justify-content:center;';
    bar.innerHTML = `<span>${message}</span>`;

    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = host.dataset.retry || 'Try again';
    retry.style.cssText = 'background:none;border:0;color:inherit;text-decoration:underline;cursor:pointer;font:inherit;';
    retry.addEventListener('click', () => {
      bar.remove();
      state.degraded = false;
      loadSession();
    });
    bar.appendChild(retry);
    document.body.appendChild(bar);
  }

  // ---- price reveal --------------------------------------------------------------

  const moneyFormat = (amountInPaise, currency) => {
    const value = amountInPaise / 100;
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(value);
    } catch (e) {
      return `₹ ${value.toFixed(2)}`;
    }
  };

  // One fetch per handle per page, shared by every card showing that product.
  const productCache = new Map();
  function fetchProduct(handle) {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      );
    }
    return productCache.get(handle);
  }

  async function revealPrice(el) {
    const handle = el.dataset.productHandle;
    if (!handle || el.dataset.revealed === 'true') return;

    const product = await fetchProduct(handle);
    if (!product) return;

    const variant = product.variants && product.variants[0];
    if (!variant) return;

    el.dataset.revealed = 'true';
    const priceEl = el.querySelector('[data-price-value]');
    const gateEl = el.querySelector('[data-price-gate]');
    const bodyEl = el.querySelector('[data-price-body]');

    if (priceEl) priceEl.textContent = moneyFormat(variant.price, product.price_currency || 'INR');

    // Stock is never shown to buyers (SHIN, 25 Sep): no in/out-of-stock line, delivery
    // promise or India / Japan quantities. It is still read, because it decides Add to Cart
    // vs enquiry and the largest quantity that can be ordered (sumitomo-product.js).
    // Every variant is inventoryPolicy DENY (Saiga-san, 18 Sep), so variant.available is
    // Shopify's "can it be ordered" answer.
    // Product cards take no action on it, so only the product page asks.
    const orderable = Boolean(variant.available);
    // With online ordering off (theme setting) nothing depends on stock, so it is not read.
    const pdp = el.closest('sumitomo-product');
    const stock = pdp && pdp.dataset.ordering !== 'off' ? await fetchStock(handle) : null;
    const state = stockState(orderable, stock);

    if (gateEl) gateEl.remove();
    if (bodyEl) {
      bodyEl.classList.remove('blur-sm', 'select-none', 'pointer-events-none');
      bodyEl.removeAttribute('aria-hidden');
    }
    el.dispatchEvent(new CustomEvent('sumitomo:price-revealed', { bubbles: true, detail: { product, variant, stock: state, stockQty: stockQuantities(stock) } }));
  }

  /* India and Japan stock from the middleware: Shopify's product JSON has one combined
     flag and no location breakdown. Used for the quantity check only, never displayed.
     On any failure the quantity check falls back to Shopify's own refusal. */
  const stockCache = new Map();
  function fetchStock(handle) {
    if (!API_BASE) return Promise.resolve(null);
    if (!stockCache.has(handle)) {
      stockCache.set(
        handle,
        fetch(`${API_BASE}/stock/${encodeURIComponent(handle)}`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      );
    }
    return stockCache.get(handle);
  }

  /** 'in' — stock at some location; 'out' — cannot be ordered (DENY at zero stock);
   *  'unknown' — orderable but the middleware could not say; 'order' — orderable with no
   *  stock, which only a variant left on CONTINUE can reach. */
  /** India and Japan only. The store also has Shopify's default "Shop location", which is
   *  not a SHIN warehouse and must neither show as a row nor count as stock. */
  function warehouses(data) {
    const all = data && Array.isArray(data.locations) ? data.locations : null;
    return all ? all.filter((loc) => /india|japan/i.test(String(loc.location))) : null;
  }

  /** { india, japan } quantities for the quantity check on the PDP, or null when the
   *  middleware sent none (older build, or unreachable) — then only Shopify checks stock. */
  function stockQuantities(data) {
    const locations = warehouses(data);
    if (!locations || !locations.length || locations.some((l) => !Number.isFinite(Number(l.quantity)))) return null;
    const sum = (re) => locations.filter((l) => re.test(String(l.location))).reduce((n, l) => n + Math.max(0, Number(l.quantity)), 0);
    return { india: sum(/india/i), japan: sum(/japan/i) };
  }

  function stockState(orderable, data) {
    if (!orderable) return 'out';
    const locations = warehouses(data);
    if (!locations || !locations.length) return 'unknown';
    return locations.some((loc) => loc.available) ? 'in' : 'order';
  }

  function applyGate() {
    if (state.tier !== 'verified') return;
    document.querySelectorAll('[data-gated-price]').forEach((el) => {
      revealPrice(el).catch(() => {});
    });
  }

  // ---- header + any element that switches on sign-in state --------------------------

  function applyHeader() {
    const signedIn = state.tier !== 'guest';
    document.querySelectorAll('[data-when-signed-in]').forEach((el) => {
      el.hidden = !signedIn;
    });
    document.querySelectorAll('[data-when-signed-out]').forEach((el) => {
      el.hidden = signedIn;
    });
    if (state.customer) {
      document.querySelectorAll('[data-customer-email]').forEach((el) => {
        el.textContent = state.customer.email;
      });
      // The header chip shows the company, matching the signed-in state in the design pack.
      document.querySelectorAll('[data-customer-company]').forEach((el) => {
        el.textContent = state.customer.company || state.customer.email;
      });
    }
  }

  document.addEventListener('sumitomo:session', () => {
    applyHeader();
    applyGate();
  });

  // Sections re-rendered by the theme editor or by cart/facet updates need re-gating.
  document.addEventListener('shopify:section:load', applyGate);
  document.addEventListener('sumitomo:products-updated', applyGate);

  // Cards that arrive AFTER the gate has run: Dawn's <product-recommendations> fetches
  // its markup once the PDP has loaded, so "You may also need" was still showing
  // "Sign in to view price & stock" to a signed-in buyer. Facet and quick-add updates
  // replace grids the same way, so watch for any inserted card rather than naming one
  // section's event.
  function watchForLateCards() {
    if (!document.body || typeof MutationObserver === 'undefined') return;
    new MutationObserver((records) => {
      if (state.tier !== 'verified') return;
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-gated-price]')) revealPrice(node).catch(() => {});
          if (node.querySelectorAll) {
            node.querySelectorAll('[data-gated-price]').forEach((el) => revealPrice(el).catch(() => {}));
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-sumitomo-logout]');
    if (!trigger) return;
    e.preventDefault();
    try {
      await fetch(`${API_BASE}/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {
      /* fall through — always send the buyer somewhere signed-out */
    }
    window.location.href = trigger.dataset.redirect || '/';
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      watchForLateCards();
      loadSession();
    });
  } else {
    watchForLateCards();
    loadSession();
  }
})();
